import * as moment from 'moment';
import { AbstractDataFilter } from './abstract-data-filter';
import { EmployeesDataSource, EmployeeDataType } from './../../data-sources/sources/employees-data-source';
import { FilterModel, QuickFilter } from '@/components/filter/FilterModel';
import store from '@/store/store';

export interface DataFilterParameters {
  filter: FilterModel;
  enabled: boolean;
}

export class DataFilter extends AbstractDataFilter {
  private sortedSourceData: EmployeeDataType[];

  constructor(private source: EmployeesDataSource) {
    super();
    this.sortedSourceData = this.source.data.sort((employee1, employee2) => employee1.Name.localeCompare(employee2.Name));
    let sortedId = 0;
    this.sortedSourceData.forEach((e) => {
      this.sortedSourceData[sortedId]._internal_datasource_id_ = sortedId;
      sortedId++; // The internal datasource Id does not correlate to an external id; it's the order of the employee list
    });
    this.filteredData = this.sortedSourceData;
  }

  protected executeInternal(parameters: DataFilterParameters) {
    const filter = parameters.filter;
    this.anyFilterApplied = parameters.enabled && !filter.isEmpty();
    if (this.anyFilterApplied) {
      this.filteredData = this.sortedSourceData.filter(
        (employee) =>
          this.simpleStringFilter(employee, filter, 'FirstName', 'firstName') &&
          this.simpleStringFilter(employee, filter, 'LastName', 'lastName') &&
          this.consultantTypeFilter(employee, filter) &&
          this.seniorityFilter(employee, filter) &&
          this.skillFilter(employee, filter) &&
          this.tribeFilter(employee, filter) &&
          this.hiveFilter(employee, filter) &&
          this.accountFilter(employee, filter) &&
          this.accountClusterFilter(employee,filter) &&
          this.startDateFilter(employee, filter) &&
          this.peopleCoachFilter(employee, filter) &&
          this.profileFilter(employee, filter) &&
          this.handelQuickFilters(employee, filter)
      ); // Employees are already sorted
    } else {
      this.filteredData = this.sortedSourceData;
    }
  }

  private simpleStringFilter(
    employee: EmployeeDataType,
    filter: FilterModel,
    employeeKey: 'FirstName' | 'LastName',
    filterKey: 'firstName' | 'lastName'
  ): boolean {
    const lowerCaseFilterString = filter[filterKey] ? filter[filterKey].toLowerCase() : null;
    return !lowerCaseFilterString || employee[employeeKey].toLowerCase().indexOf(lowerCaseFilterString) !== -1;
  }

  private consultantTypeFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.consultantTypes || filter.consultantTypes.length === 0) {
      return true;
    }
    return filter.consultantTypes.some((type) => type.value === employee.TechNonTech);
  }

  private seniorityFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.seniority || filter.seniority.length === 0) {
      return true;
    }
    return filter.seniority.some((sen) => employee.FunctionLevel === sen.value);
  }

  private skillFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.skills || filter.skills.length === 0) {
      return true;
    }
    const employeeSkillIds = store.state.dataSourceLoader.employeesDataSource.employeeIdToSkillIds[employee.Id];
    return (
      employeeSkillIds &&
      employeeSkillIds.length &&
      employeeSkillIds.some((skillId) => filter.skills.some((skillFilterItem) => skillFilterItem.value === skillId))
    );
  }

  private tribeFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.tribes || filter.tribes.length === 0) {
      return true;
    }
    return filter.tribes.some((tribe) => tribe.value === employee.TribeId); // Also works for no tribe as tribeId on employee is null
  }

  private hiveFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.hives || filter.hives.length === 0) {
      return true;
    }
    return filter.hives.some((hive) => hive.value === employee.HiveId);
  }

  private accountFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.accounts || filter.accounts.length === 0) {
      return true;
    }
    const employeeAllocations = store.state.dataSourceLoader.employeesToAllocationsDataSource.employeeIdToAllocations[employee.Id];
    return (
      employeeAllocations &&
      employeeAllocations.length &&
      employeeAllocations.some((allocation) => filter.accounts.some((account) => account.value === allocation.AccountId))
    );
  }

  private accountClusterFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.accountClusters || filter.accountClusters.length === 0) {
      return true;
    }
    const employeeAllocations = store.state.dataSourceLoader.employeesToAllocationsDataSource.employeeIdToAllocations[employee.Id];
    return (
      employeeAllocations &&
      employeeAllocations.length &&
      employeeAllocations.some((allocation) => filter.accountClusters.some((ac) => ac.value === allocation.AccountClusterId))
    );
  }

  private startDateFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!employee.StartDate) {
      return false;
    }
    const startDate = moment(employee.StartDate).startOf('day');
    return (
      (!filter.startDateFrom || filter.startDateFrom.startOf('day').isSameOrBefore(startDate)) &&
      (!filter.startDateTo || filter.startDateTo.startOf('day').isSameOrAfter(startDate))
    );
  }

  private peopleCoachFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.peopleCoaches || filter.peopleCoaches.length === 0) {
      return true;
    }
    return employee.CoachRoles.some(role => filter.peopleCoaches.some(coach => coach.value === role));
  }

  private profileFilter(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.profiles || filter.profiles.length === 0) {
      return true;
    }

    return filter.profiles.some(profile => profile.label === employee.Profile);
  }

  private handelQuickFilters(employee: EmployeeDataType, filter: FilterModel) {
    if (!filter.quickFilters || filter.quickFilters.length === 0) {
      return true;
    }

    if (filter.quickFilters.some(quickFilter => quickFilter.value === QuickFilter.NewHires)) {
      if (!employee.StartDate) {
        return false;
      }
      const startDateEmployee = moment(employee.StartDate).startOf('day').startOf('day');
      const threeMonthsAgo = moment().subtract(3, "months").startOf('day');

      return startDateEmployee.isSameOrAfter(threeMonthsAgo);
    }

    return false;
  }
}
